/**
 * Previsão de modelo publicada pela máquina do agendador
 * (`AmaView-dados/agendador/previsao`): o tempo do ECMWF (IFS e AIFS, dados
 * abertos, 0,25°, até +15 dias) e a fumaça do CAMS (Copernicus, 0,4°, até
 * +5 dias), recortados na Amazônia Legal.
 *
 * Formato (o mesmo do cabeçalho de `previsao.py`): grades uint16
 * little-endian, valor = `off + esc × q`, 65535 = sem dado; linha i →
 * lat = lat0 − i·passo, coluna j → lon = lon0 + j·passo (o CENTRO do ponto).
 * Um arquivo por variável e passo para o mapa; blocos de 2° × 2° com todos os
 * passos e variáveis para a ficha do ponto. Tudo imutável por rodada; só o
 * `indice.json` muda.
 *
 * Os cenários do Google WeatherNext 2 (`wn2`) ficam numa pasta à parte, atrás
 * de senha (os termos do Google para a previsão em tempo real só permitem a
 * distribuição controlada a pessoas identificadas): índice próprio
 * (`wn2/indice.json`) e o cabeçalho `Authorization` em todo pedido
 * (`acesso.ts` guarda a credencial).
 */
import { fetchWithRetry } from '../lib/fetchRetry';

/** Onde a máquina publica. `VITE_PREVISAO_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_PREVISAO = (import.meta.env.VITE_PREVISAO_URL ?? 'https://147.15.84.134/previsao').replace(/\/$/, '');

export const VERSAO = 1;

/** Atribuições exigidas pelas licenças (CC BY 4.0 do ECMWF e do DWD; a frase do Copernicus). */
export const ATRIBUICAO_IFS = 'Previsão: ECMWF IFS (dados abertos, CC BY 4.0)';
export const ATRIBUICAO_AIFS = 'Previsão: ECMWF AIFS, modelo de IA (dados abertos, CC BY 4.0)';
export const ATRIBUICAO_GFS = 'Previsão: NOAA GFS (domínio público)';
export const ATRIBUICAO_ICON = 'Previsão: ICON — Fonte: Deutscher Wetterdienst (DWD), CC BY 4.0';
export const ATRIBUICAO_CAMS = 'Fumaça prevista: CAMS/Copernicus — Contains modified Copernicus Atmosphere Monitoring Service information';
/** A citação curta do WeatherNext 2 no mapa; a inteira (exigida pelos termos) fica nos ajustes e na ficha (`TermosWn2`). */
export const ATRIBUICAO_WN2 = 'Cenários: © 2024-6 Google LLC, WeatherNext 2 (dados experimentais, termos do Google) via dynamical.org';

/** Os termos do Google para a previsão em tempo real do WeatherNext 2. */
export const TERMOS_WN2_URL = 'https://storage.googleapis.com/weathernext-public/terms-of-use.pdf';

export type ModeloId = 'ifs' | 'aifs' | 'gfs' | 'icon' | 'cams' | 'wn2';

/**
 * O modelo do TEMPO (chuva, temperatura, vento): IFS (o padrão), AIFS (o de IA
 * do ECMWF), GFS (NOAA) ou ICON (DWD). A fumaça é sempre do CAMS e os
 * cenários, do WeatherNext 2.
 */
export type ModeloTempo = 'ifs' | 'aifs' | 'gfs' | 'icon';
/** Na ordem do botão. */
export const MODELOS_TEMPO: readonly ModeloTempo[] = ['ifs', 'aifs', 'gfs', 'icon'];

export interface SobreModeloTempo {
  /** Sigla (botão). */
  curto: string;
  /** Nome com a instituição (relógio, ficha, exportação). */
  nome: string;
  /** Uma linha do que o distingue (caixa do celular). */
  linha: string;
  /** O que o distingue, completo (dica do botão, leitor de tela). */
  detalhe: string;
  /** Modelo de IA (o selo diz "(IA)"). */
  ia: boolean;
  /** A rajada que o modelo publica (null = não tem). */
  rajada: string | null;
  /** Fonte, para o link do rodapé da ficha. */
  url: string;
}

export const SOBRE_MODELO_TEMPO: Record<ModeloTempo, SobreModeloTempo> = {
  ifs: {
    curto: 'IFS',
    nome: 'ECMWF IFS',
    linha: 'ECMWF · 15 dias',
    detalhe: 'modelo físico do ECMWF (Europa) · passos de 3 h nos primeiros 6 dias · até 15 dias',
    ia: false,
    rajada: 'máxima do intervalo',
    url: 'https://www.ecmwf.int/en/forecasts/datasets/open-data',
  },
  aifs: {
    curto: 'AIFS',
    nome: 'ECMWF AIFS',
    linha: 'ECMWF · IA · sem rajada',
    detalhe: 'modelo de IA do ECMWF · passos de 6 h · sem rajada · tende a suavizar a chuva forte · até 15 dias',
    ia: true,
    rajada: null,
    url: 'https://www.ecmwf.int/en/forecasts/datasets/open-data',
  },
  gfs: {
    curto: 'GFS',
    nome: 'NOAA GFS',
    linha: 'NOAA (EUA) · 16 dias',
    detalhe: 'modelo físico da NOAA (Estados Unidos) · passos de 3 h · rajada do instante · até 16 dias',
    ia: false,
    rajada: 'no instante',
    url: 'https://www.nco.ncep.noaa.gov/pmb/products/gfs/',
  },
  icon: {
    curto: 'ICON',
    nome: 'DWD ICON',
    linha: 'DWD · 13 km · 7,5 dias',
    detalhe: 'modelo físico do DWD (Alemanha) · ~13 km, a grade mais fina · passos de 3 h · até 7,5 dias',
    ia: false,
    rajada: 'máxima da última hora (até +78 h; depois, das últimas 3 h)',
    url: 'https://opendata.dwd.de/weather/nwp/icon/',
  },
};

/** Os modelos do tempo que o índice tem (com alguma rodada), na ordem do botão. */
export function modelosTempoNoIndice(indice: IndicePrevisao | null): ModeloTempo[] {
  return MODELOS_TEMPO.filter((m) => (indice?.modelos[m]?.rodadas.length ?? 0) > 0);
}

/** O modelo do tempo em uso: o escolhido, se o índice o tem; senão o IFS; senão o primeiro que houver. */
export function modeloTempoEmUso(escolhido: ModeloTempo, indice: IndicePrevisao | null): ModeloTempo {
  const tem = modelosTempoNoIndice(indice);
  if (tem.includes(escolhido)) return escolhido;
  return tem.includes('ifs') ? 'ifs' : (tem[0] ?? 'ifs');
}

/** Há escolha (dois ou mais modelos do tempo no índice)? O botão "Modelo" só aparece assim. */
export function temEscolhaDeModelo(indice: IndicePrevisao | null): boolean {
  return modelosTempoNoIndice(indice).length > 1;
}

/** "~28 km" (0,25°) ou "~14 km" (0,125°): o lado da área que um valor representa. */
export function ladoDaCelulaKm(passo: number): string {
  return `~${Math.round(passo * 111)} km`;
}

export interface VariavelModelo {
  /** Unidade publicada (°C, %, mm, m/s, µg/m³; vazio no AOD). */
  u: string;
  off: number;
  esc: number;
  desc: string;
}

export interface GradeModelo {
  lat0: number;
  lon0: number;
  passo: number;
  /** Linhas (latitudes). */
  ni: number;
  /** Colunas (longitudes). */
  nj: number;
  /** Pontos por lado de um bloco da série. */
  bloco: number;
}

export interface Modelo {
  id: ModeloId;
  fonte: string;
  /** Rodadas no disco, da mais nova para a mais velha (`AAAAMMDDHH`). */
  rodadas: string[];
  /** Antecedências (h) de cada passo. */
  passos: number[];
  grade: GradeModelo;
  /** Na ordem dos blocos da série (a ordem do servidor). */
  vars: Record<string, VariavelModelo>;
  ordemVars: string[];
  erro: string | null;
}

/** O que os termos do Google pedem que acompanhe o WeatherNext 2 (do `wn2/indice.json`). */
export interface TermosWn2 {
  url: string;
  aviso: string;
  copyright: string;
  citacao: string;
  modificacoes: string;
}

export interface IndicePrevisao {
  gerado: number | null;
  semDado: number;
  modelos: Partial<Record<ModeloId, Modelo>>;
  /** Só com o acesso de pesquisa (o índice do WeatherNext 2 juntado ao público). */
  termosWn2?: TermosWn2;
}

const MODELOS: readonly ModeloId[] = ['ifs', 'aifs', 'gfs', 'icon', 'cams', 'wn2'];

const numero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Interpreta o índice; um modelo malformado some sem derrubar o outro. */
export function lerIndice(cru: unknown): IndicePrevisao {
  const r = cru as { gerado?: unknown; semDado?: unknown; modelos?: unknown };
  if (!r || typeof r !== 'object' || !r.modelos || typeof r.modelos !== 'object') throw new Error('índice da previsão sem `modelos`');
  const modelos: Partial<Record<ModeloId, Modelo>> = {};
  for (const id of MODELOS) {
    const m = (r.modelos as Record<string, Record<string, unknown>>)[id];
    if (!m) continue;
    const g = m.grade as Record<string, unknown> | undefined;
    const vars = m.vars as Record<string, Record<string, unknown>> | undefined;
    if (!g || !vars || !Array.isArray(m.passos) || !Array.isArray(m.rodadas)) continue;
    if (![g.lat0, g.lon0, g.passo, g.ni, g.nj, g.bloco].every(numero)) continue;
    const lidas: Record<string, VariavelModelo> = {};
    for (const [nome, v] of Object.entries(vars)) {
      if (v && numero(v.off) && numero(v.esc) && v.esc > 0) lidas[nome] = { u: String(v.u ?? ''), off: v.off, esc: v.esc, desc: String(v.desc ?? '') };
    }
    const passos = (m.passos as unknown[]).filter(numero);
    // Uma rodada é AAAAMMDDHH; a ordem é a do servidor (mais nova primeiro), conferida.
    const rodadas = (m.rodadas as unknown[]).filter((x): x is string => typeof x === 'string' && /^\d{10}$/.test(x)).sort().reverse();
    modelos[id] = {
      id,
      fonte: String(m.fonte ?? ''),
      rodadas,
      passos,
      grade: g as unknown as GradeModelo,
      vars: lidas,
      ordemVars: Object.keys(vars).filter((k) => k in lidas),
      erro: typeof m.erro === 'string' ? m.erro : null,
    };
  }
  const gerado = typeof r.gerado === 'string' ? Date.parse(r.gerado) : NaN;
  return { gerado: Number.isFinite(gerado) ? gerado : null, semDado: numero(r.semDado) ? r.semDado : 65535, modelos };
}

/** Instante (epoch ms, UTC) do início de uma rodada `AAAAMMDDHH`. */
export function instanteDaRodada(rodada: string): number {
  return Date.UTC(+rodada.slice(0, 4), +rodada.slice(4, 6) - 1, +rodada.slice(6, 8), +rodada.slice(8, 10));
}

export function urlIndice(base: string = BASE_PREVISAO): string {
  return `${base}/v${VERSAO}/indice.json`;
}

/** O índice do WeatherNext 2 (com senha). */
export function urlIndiceWn2(base: string = BASE_PREVISAO): string {
  return `${base}/v${VERSAO}/wn2/indice.json`;
}

export function urlMapa(modelo: ModeloId, rodada: string, variavel: string, passo: number, base: string = BASE_PREVISAO): string {
  return `${base}/v${VERSAO}/${modelo}/${rodada}/mapa/${variavel}/${String(passo).padStart(3, '0')}.bin`;
}

export function urlBloco(modelo: ModeloId, rodada: string, bi: number, bj: number, base: string = BASE_PREVISAO): string {
  return `${base}/v${VERSAO}/${modelo}/${rodada}/serie/${String(bi).padStart(2, '0')}_${String(bj).padStart(2, '0')}.bin`;
}

/** Bytes de uma grade uint16 little-endian (o navegador pode ser big-endian em tese; DataView não depende disso). */
export function lerUint16(buf: ArrayBuffer): Uint16Array {
  const n = buf.byteLength >> 1;
  const out = new Uint16Array(n);
  const dv = new DataView(buf);
  for (let k = 0; k < n; k++) out[k] = dv.getUint16(k * 2, true);
  return out;
}

/** Valores reais (NaN onde não há dado). */
export function decodificar(q: Uint16Array, v: Pick<VariavelModelo, 'off' | 'esc'>, semDado = 65535): Float32Array {
  const out = new Float32Array(q.length);
  for (let k = 0; k < q.length; k++) out[k] = q[k] === semDado ? NaN : v.off + v.esc * q[k];
  return out;
}

/** O índice muda quando entra rodada nova; `no-cache` revalida (o nginx responde 304). */
export async function carregarIndice(signal?: AbortSignal): Promise<IndicePrevisao> {
  const cru = await fetchWithRetry<unknown>(urlIndice(), { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

// ---------------------------------------------------------------------------
// Acesso de pesquisa (WeatherNext 2)

let credencialWn2: string | null = null;

/** `Basic …` do acesso de pesquisa, ou null (quem guarda é `acesso.ts`). */
export function definirCredencialWn2(c: string | null): void {
  credencialWn2 = c;
}

/** Cabeçalhos do modelo: o WeatherNext 2 vai com a credencial (sem ela, o pedido nem sai). */
function cabecalhos(modelo: ModeloId): HeadersInit | undefined {
  if (modelo !== 'wn2') return undefined;
  if (!credencialWn2) throw new Error('sem acesso de pesquisa');
  return { Authorization: credencialWn2 };
}

/** Lê o índice do WeatherNext 2 com uma credencial (`Basic …`); `HttpError` 401 = usuário ou senha errados. */
export async function carregarIndiceWn2(credencial: string, signal?: AbortSignal): Promise<{ modelo: Modelo; termos: TermosWn2 | undefined }> {
  const cru = await fetchWithRetry<unknown>(
    urlIndiceWn2(),
    { cache: 'no-cache', mode: 'cors', credentials: 'omit', headers: { Authorization: credencial } },
    { signal, read: (r) => r.json() as Promise<unknown> },
  );
  const indice = lerIndice(cru);
  const modelo = indice.modelos.wn2;
  if (!modelo) throw new Error('índice do WeatherNext 2 sem o modelo');
  const t = (cru as { termos?: Record<string, unknown> }).termos;
  const termos =
    t && typeof t.url === 'string'
      ? { url: t.url, aviso: String(t.aviso ?? ''), copyright: String(t.copyright ?? ''), citacao: String(t.citacao ?? ''), modificacoes: String(t.modificacoes ?? '') }
      : undefined;
  return { modelo, termos };
}

async function binario(url: string, modelo: ModeloId, signal?: AbortSignal): Promise<ArrayBuffer> {
  return fetchWithRetry<ArrayBuffer>(url, { mode: 'cors', credentials: 'omit', headers: cabecalhos(modelo) }, { signal, read: (r) => r.arrayBuffer() });
}

/** Uma variável num passo, em valores reais (NaN = sem dado). */
export async function carregarPasso(
  m: Modelo,
  rodada: string,
  variavel: string,
  passo: number,
  semDado: number,
  signal?: AbortSignal,
): Promise<Float32Array> {
  const v = m.vars[variavel];
  if (!v) throw new Error(`variável ${variavel} fora do ${m.id}`);
  const q = lerUint16(await binario(urlMapa(m.id, rodada, variavel, passo), m.id, signal));
  if (q.length !== m.grade.ni * m.grade.nj) throw new Error(`grade de ${q.length} pontos, esperado ${m.grade.ni * m.grade.nj}`);
  return decodificar(q, v, semDado);
}

/** Bloco da série cru: [variável][passo][linha][coluna], uint16. */
export async function carregarBloco(m: Modelo, rodada: string, bi: number, bj: number, signal?: AbortSignal): Promise<Uint16Array> {
  const q = lerUint16(await binario(urlBloco(m.id, rodada, bi, bj), m.id, signal));
  const b = m.grade.bloco;
  const esperado = m.ordemVars.length * m.passos.length * b * b;
  if (q.length !== esperado) throw new Error(`bloco de ${q.length} valores, esperado ${esperado}`);
  return q;
}
